// Third-party
import React from 'react';
import classNames from 'classnames';

// Components
import { Tooltip } from '../tooltips';
import FilterDropdown, { DropdownGroupSelection } from './FilterDropdown';
import RoundedPastille from '@/views/components/misc/RoundedPastille';
import { Pastille } from '../misc';

// Types
import DropdownOptionsGroup from '@/types/dropdown-options-group';
import FilterOption from '@/types/filter-option';

// Legacy Component
import LegacyFilter from './LegacyFilter';
import withLegacyVersion from '../hocs/withLegacyVersion';

interface Props {
  multiselect: boolean;
  optionsGroups: DropdownOptionsGroup[];
  onChange: (value: FilterOption[]) => void;
  search?: boolean;
  className?: string;
  loading?: boolean;
  tooltip?: string;
  label?: string;
  dark?: boolean;
  'data-private'?: boolean;
  'data-dd-privacy'?: 'allow' | 'mask' | 'hidden' | 'mask-user-input';
  value?: FilterOption[];
}

interface State {
  isOpen: boolean;
  value: FilterOption[];
}

class Filter extends React.Component<Props, State> {
  private container = React.createRef<HTMLDivElement>();

  private filterButton = React.createRef<HTMLButtonElement>();

  static defaultProps = {
    multiselect: false,
    optionsGroups: [],
    onChange: () => {},
  };

  readonly state: State = {
    isOpen: false,
    value: [],
  };

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
  }

  handleClickOutside = (event: Event) => {
    if (!(event.target instanceof Node) || !this.container.current) {
      return;
    }

    if (!this.container.current.contains(event.target)) {
      this.setState({ isOpen: false });
    }
  };

  onChange = (value: FilterOption[]) => {
    this.setState({ isOpen: this.props.multiselect, value });
    this.props.onChange(value);
  };

  clearFilter = () => {
    this.setState({ value: [] });
  };

  onToggleFilter = () => {
    this.setState(prevState => ({
      isOpen: !prevState.isOpen,
    }));
    this.filterButton.current?.focus();
  };

  getValue = () => {
    // Filter supports both controlled and uncontrolled behaviors.
    // Prioritize value from props if defined, otherwise use local
    // state value.
    if (this.props.value !== undefined) {
      return this.props.value;
    }

    return this.state.value;
  };

  renderText = () => {
    const { multiselect, optionsGroups } = this.props;
    const value = this.getValue();

    if (value.length === 0) {
      return null;
    }

    const renderLabel = (label?: string) => <>{label ? <Pastille>{label}</Pastille> : null}</>;

    if (!multiselect) {
      return renderLabel(value[0].label);
    }

    const group = optionsGroups.find(group => group.options.some(option => option.value === value[0].value));
    if (group?.selection === DropdownGroupSelection.Radio) {
      return renderLabel(value[0].label);
    }

    return <RoundedPastille>{value.length.toString()}</RoundedPastille>;
  };

  renderDropdown = () => {
    const { dark, label } = this.props;

    const classes = classNames('filter-button', {
      focused: this.state.isOpen,
      active: this.getValue().length > 0,
    });

    const labelElement = (
      <div className="flex-grow-1 filter-text">
        <span>{label}</span>
      </div>
    );

    return (
      <button ref={this.filterButton} type="button" className={classNames(classes, { dark })} onClick={this.onToggleFilter}>
        {this.props.tooltip == null ? labelElement : <Tooltip message={this.props.tooltip}>{labelElement}</Tooltip>}
        <div>{this.renderText()}</div>
        <div className="dropdown-trigger">
          <i className="icon-cheveron-down" />
        </div>
      </button>
    );
  };

  render() {
    return (
      <div
        data-private={this.props['data-private']}
        data-dd-privacy={this.props['data-dd-privacy']}
        className={classNames('filter filter-v2', this.props.className)}
        ref={this.container}
      >
        {this.renderDropdown()}

        {this.state.isOpen && (
          <FilterDropdown
            clearOption
            search={this.props.search}
            optionsGroups={this.props.optionsGroups}
            multiselect={this.props.multiselect}
            value={this.getValue()}
            onChange={this.onChange}
            onToggleFilter={this.onToggleFilter}
            loading={this.props.loading}
            visibleCount={7}
          />
        )}
      </div>
    );
  }
}

export interface FilterInterface {
  clearFilter: () => void;
}

// Remove this line when removing the legacy version
export default withLegacyVersion(Filter, LegacyFilter);
// And replace it by this line
// export default Filter;
